<script lang="ts">
  import Select from "svelte-select"
  import { fetchCustomers } from "../../../../../../../../../../../../api/kehrblatt/customer-registry/customers"

  type CustomerSelectItem = {
    value: string
    label: string
    customer: Customer
  }

  type Props = {
    organizationId: string
    seedCustomers?: Customer[]
    disabled?: boolean
    brightBackground?: boolean
    id?: string
    placeholder?: string
    onChange?: (customer: Customer | null) => void
  }

  type PublicProps = Props & {
    value?: string
    selectedCustomer?: Customer | null
  }

  const minimumSearchLength = 2
  const searchLimit = 25

  let {
    organizationId,
    seedCustomers = [],
    disabled = false,
    brightBackground = true,
    id = "customer-async-select",
    placeholder = "Ansprechpartner wählen",
    onChange = undefined,
    value = $bindable(""),
    selectedCustomer = $bindable<Customer | null>(null),
  }: PublicProps = $props()

  let items = $state<CustomerSelectItem[]>([])
  let selectedItem = $state<CustomerSelectItem | null>(null)
  let filterText = $state("")
  let loading = $state(false)
  let syncingSelectedItem = false
  let lastAppliedValueKey = $state<string | null>(null)
  let lastAppliedSelectionKey = $state<string | null>(null)
  let loadedCustomers = $state<Customer[]>([])

  const pendingCustomerIds = new Set<string>()

  const normalizeId = (input: string | null | undefined) => `${input ?? ""}`.trim()

  const mapCustomerToItem = (customer: Customer): CustomerSelectItem => ({
    value: normalizeId(customer.id),
    label: customer.display_name || customer.id,
    customer,
  })

  const mergeCachedCustomers = (current: Customer[], incoming: Customer[]) => {
    if (!incoming.length) return current

    const byId = new Map(current.map((entry) => [normalizeId(entry.id), entry]))
    let changed = false
    for (const customer of incoming) {
      const customerId = normalizeId(customer.id)

      if (!customerId) continue

      if (byId.get(customerId) !== customer) {
        byId.set(customerId, customer)
        changed = true
      }
    }

    return changed ? Array.from(byId.values()) : current
  }

  const updateSelectedItem = (customer: Customer | null) => {
    const nextItem = customer ? mapCustomerToItem(customer) : null
    const currentValue = selectedItem?.value ?? ""
    const nextValue = nextItem?.value ?? ""

    if (currentValue === nextValue) return

    syncingSelectedItem = true
    selectedItem = nextItem
    queueMicrotask(() => {
      syncingSelectedItem = false
    })
  }

  const cachedCustomers = $derived.by(() => {
    const withSeeds = mergeCachedCustomers([], seedCustomers)
    const withLoaded = mergeCachedCustomers(withSeeds, loadedCustomers)

    return selectedCustomer ? mergeCachedCustomers(withLoaded, [selectedCustomer]) : withLoaded
  })
  const currentCustomerId = $derived(normalizeId(value))
  const currentCustomer = $derived.by(() => {
    const fromCache =
      cachedCustomers.find((entry) => normalizeId(entry.id) === currentCustomerId) ?? null

    if (fromCache) return fromCache

    if (selectedCustomer && normalizeId(selectedCustomer.id) === currentCustomerId)
      return selectedCustomer

    return null
  })

  const emptyMessage = $derived.by(() => {
    if (loading) return "Suche läuft…"

    if (filterText.trim().length < minimumSearchLength) return "Mindestens 2 Zeichen für Ergebnisse"

    return "Keine Ergebnisse"
  })

  const ensureCustomerLoaded = async (customerId: string) => {
    if (!organizationId || !customerId || pendingCustomerIds.has(customerId)) return

    pendingCustomerIds.add(customerId)
    try {
      const loaded = await fetchCustomers({
        organizationId,
        customerIds: [customerId],
        limit: 1,
      })
      loadedCustomers = mergeCachedCustomers(loadedCustomers, loaded?.items ?? [])
    } catch (err) {
      console.error("customer_async_select_load_failed", err)
    } finally {
      pendingCustomerIds.delete(customerId)
    }
  }

  const loadOptions = async (input: string) => {
    const trimmed = input.trim()

    if (trimmed.length < minimumSearchLength) {
      items = []

      return []
    }

    loading = true
    try {
      const response = await fetchCustomers({
        organizationId,
        query: trimmed,
        limit: searchLimit,
      })
      const nextItems = (response?.items ?? []).map(mapCustomerToItem)
      loadedCustomers = mergeCachedCustomers(loadedCustomers, response?.items ?? [])
      items = nextItems

      return nextItems
    } catch (err) {
      console.error("customer_async_select_search_failed", err)
      items = []

      return []
    } finally {
      loading = false
    }
  }

  $effect(() => {
    if (currentCustomerId === lastAppliedValueKey) return

    if (!currentCustomerId) {
      if (selectedCustomer !== null) selectedCustomer = null

      updateSelectedItem(null)
      lastAppliedValueKey = currentCustomerId
      lastAppliedSelectionKey = ""

      return
    }

    if (currentCustomer) {
      if (selectedCustomer !== currentCustomer) {
        selectedCustomer = currentCustomer
      }

      updateSelectedItem(currentCustomer)
      lastAppliedValueKey = currentCustomerId
      lastAppliedSelectionKey = currentCustomerId

      return
    }

    void ensureCustomerLoaded(currentCustomerId)
  })

  const applySelection = (nextCustomer: Customer | null) => {
    if (syncingSelectedItem) return

    const nextCustomerId = normalizeId(nextCustomer?.id)

    if (nextCustomerId === lastAppliedSelectionKey) return

    selectedCustomer = nextCustomer
    value = nextCustomerId
    lastAppliedValueKey = nextCustomerId
    lastAppliedSelectionKey = nextCustomerId
    onChange?.(nextCustomer)
  }

  const handleSelectChange = (event: CustomEvent<CustomerSelectItem | null>) => {
    filterText = ""
    applySelection(event.detail?.customer ?? selectedItem?.customer ?? null)
  }

  const handleSelectClear = () => {
    filterText = ""
    applySelection(null)
  }
</script>

<label class="fieldShell" data-input-id={id} class:disabled>
  <span class="labelspan hasValue">{placeholder}</span>
  <div class="select-input-shell">
    <Select
      bind:value={selectedItem}
      bind:filterText
      {disabled}
      {loading}
      {loadOptions}
      {items}
      {id}
      placeholder=" "
      clearable={true}
      debounceWait={300}
      class={brightBackground ? "svelte-select--bright" : "svelte-select--dark"}
      itemId="value"
      label="label"
      showChevron={true}
      closeListOnChange={true}
      inputAttributes={{ autocomplete: "off" }}
      on:change={handleSelectChange}
      on:clear={handleSelectClear}
    >
      <svelte:fragment slot="selection" let:selection>
        <div class="select-selection">
          <span class="select-selection__label"
            >{selection?.label ?? selection?.name ?? selection?.value ?? ""}</span
          >
        </div>
      </svelte:fragment>

      <svelte:fragment slot="item" let:item>
        <div class="select-item">
          <span class="select-item__label">{item.label ?? item.name ?? item.value}</span>
        </div>
      </svelte:fragment>

      <svelte:fragment slot="empty">
        <div class="emptyState">{emptyMessage}</div>
      </svelte:fragment>
    </Select>
  </div>
</label>

<style lang="less">
  .fieldShell {
    width: 100%;
    max-width: 100%;
  }

  :global(label.fieldShell.error .svelte-select) {
    border-color: var(--warning-100);
  }

  .fieldShell :global(.svelte-select),
  .fieldShell :global(.select-input-shell) {
    width: 100%;
  }

  .emptyState {
    padding: 0.6rem 0.85rem;
    color: color-mix(in srgb, var(--text-100) 72%, transparent);
    font-size: 0.92rem;
  }
</style>
